'use client';

/**
 * The parent report.
 *
 * Honest by construction: every figure is derived from the same session log
 * the child's own dashboard uses, and where the data is thin the report says
 * so rather than filling the space with a confident-looking chart.
 *
 * "Download PDF" is the browser's own print-to-PDF. That is not a compromise —
 * it needs no library, produces a proper vector document, respects the user's
 * page size, and works offline. A print stylesheet in styles/kids.css strips
 * the navigation and forces a white background.
 */
import React, { useState } from 'react';
import Link from 'next/link';
import { Printer, ChevronDown } from 'lucide-react';
import { useKids } from '../context/KidsProvider';
import {
  overview,
  periodReport,
  weakKeys,
  dailyHistory,
  suggestions,
  levelProgress,
} from '../lib/analytics';
import { benchmarkFor } from '../lib/metrics';
import { MAX_STARS } from '../lib/progress';
import { PracticeChart, SpeedChart } from './Charts';
import { speedSeries } from '../lib/analytics';
import Avatar from './Avatar';
import { StatTile, ProgressRail, LoadingBlock, EmptyState } from './ui';

export default function ParentReport() {
  const { hydrated, store, profile, switchProfile } = useKids();
  const [range, setRange] = useState<7 | 30 | 90>(30);

  if (!hydrated) return <LoadingBlock height={420} />;
  if (!profile) return null;

  const stats = overview(profile);
  const report = periodReport(
    profile,
    range,
    range === 7 ? 'This week' : range === 30 ? 'This month' : 'Last 3 months'
  );
  const weak = weakKeys(profile, 5);
  const benchmark = benchmarkFor(profile.ageBand);
  const levels = levelProgress(profile);
  const tips = suggestions(profile).filter((t) => t.tone !== 'praise');

  if (stats.totalSessions === 0) {
    return (
      <EmptyState
        emoji="📋"
        title="No practice recorded yet"
        body="Once your child completes a lesson, this page shows their speed, accuracy, practice time, weak keys and a printable report."
        href="/kids/journey"
        cta="Open the journey"
      />
    );
  }

  return (
    <div className="grid gap-6">
      {/* ── controls (never printed) ────────────────────────────────────── */}
      <div className="k-no-print flex flex-wrap items-center gap-3 justify-between">
        {store.profiles.length > 1 && (
          <label className="flex items-center gap-2 text-sm font-bold">
            Child
            <span className="relative">
              <select
                value={profile.id}
                onChange={(e) => switchProfile(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 rounded-xl font-bold"
                style={{
                  background: 'var(--input)',
                  border: '2px solid var(--border-strong)',
                  color: 'var(--foreground)',
                }}
              >
                {store.profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={15}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                aria-hidden="true"
              />
            </span>
          </label>
        )}

        <div className="flex items-center gap-2">
          <div
            role="group"
            aria-label="Report period"
            className="inline-flex p-1 rounded-full"
            style={{ background: 'var(--muted)' }}
          >
            {([7, 30, 90] as const).map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setRange(days)}
                aria-pressed={range === days}
                className="k-btn k-btn-sm"
                style={
                  range === days
                    ? {
                        background: 'var(--card)',
                        color: 'var(--accent)',
                        boxShadow: 'var(--k-shadow-sm)',
                      }
                    : {
                        background: 'transparent',
                        color: 'var(--muted-foreground)',
                        boxShadow: 'none',
                      }
                }
              >
                {days === 7 ? 'Week' : days === 30 ? 'Month' : '3 months'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="k-btn k-btn-primary k-btn-sm"
          >
            <Printer size={16} aria-hidden="true" /> Print / save PDF
          </button>
        </div>
      </div>

      {/* ── the report sheet ────────────────────────────────────────────── */}
      <div className="k-card-lg p-6 k-print-sheet">
        <header
          className="flex items-center gap-4 pb-5 mb-5"
          style={{ borderBottom: '2px solid var(--border)' }}
        >
          <Avatar config={profile.avatar} size={56} />
          <div className="min-w-0 flex-1">
            <p className="k-eyebrow">Typing progress report · {report.label}</p>
            <h2 className="text-xl">{profile.name}</h2>
            <p className="text-sm text-muted-foreground">
              Generated {new Date().toLocaleDateString(undefined, { dateStyle: 'long' })} ·
              TypeSwift Kids Typing Journey
            </p>
          </div>
        </header>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile
            label="Practice time"
            value={`${report.minutes}m`}
            sub={`${report.activeDays} active days`}
            emoji="⏱️"
            color="--k-grape"
          />
          <StatTile
            label="Lessons"
            value={report.sessions}
            sub={`${report.lessons} different lessons`}
            emoji="📚"
            color="--k-sky"
          />
          <StatTile
            label="Average speed"
            value={`${report.averageWpm}`}
            sub={`best ${report.bestWpm} wpm`}
            emoji="⚡"
            color="--k-mint"
          />
          <StatTile
            label="Accuracy"
            value={`${report.averageAccuracy}%`}
            sub="across the period"
            emoji="🎯"
            color="--k-mango"
          />
        </div>

        {/* ── plain-English summary ─────────────────────────────────────── */}
        <section className="mt-6">
          <h3 className="text-base mb-2">In plain English</h3>
          <p className="text-muted-foreground leading-relaxed">
            {profile.name} practised on{' '}
            <strong className="text-foreground">
              {report.activeDays} of the last {range} days
            </strong>
            , for {report.minutes} minutes in total, and completed {report.sessions} lesson
            {report.sessions === 1 ? '' : 's'}. Their typing speed over that period averaged{' '}
            <strong className="text-foreground">{report.averageWpm} words per minute</strong> at{' '}
            {report.averageAccuracy}% accuracy.
            {report.averageWpm > 0 && (
              <>
                {' '}
                For {benchmark.label.toLowerCase()}, around {benchmark.typical} wpm is typical and{' '}
                {benchmark.strong} wpm is strong.
              </>
            )}{' '}
            Overall they have finished {stats.lessonsDone} of {stats.lessonsTotal} lessons in the
            course and collected {stats.stars} of {MAX_STARS} stars.
            {stats.trendPct !== 0 && (
              <>
                {' '}
                Since starting, their speed has changed by{' '}
                <strong className="text-foreground">
                  {stats.trendPct > 0 ? '+' : ''}
                  {stats.trendPct}%
                </strong>
                .
              </>
            )}
          </p>
          {report.sessions < 5 && (
            <p className="text-sm mt-3 p-3 rounded-xl" style={{ background: 'var(--muted)' }}>
              Note: with fewer than five lessons in this period, these averages move around a lot.
              Treat them as a rough indication rather than a measurement.
            </p>
          )}
        </section>

        {/* ── needs work ────────────────────────────────────────────────── */}
        <section className="mt-6">
          <h3 className="text-base mb-2">Where the practice should go</h3>
          {weak.length === 0 ? (
            <p className="text-muted-foreground">
              No key is currently below 92% accuracy with enough attempts to be meaningful. The most
              useful next step is simply the next lesson in the journey.
            </p>
          ) : (
            <>
              <p className="text-muted-foreground mb-3">
                These keys are the least accurate, counting only keys pressed at least six times:
              </p>
              <ul className="grid gap-2">
                {weak.map((key) => (
                  <li key={key.char} className="flex items-center gap-3 text-sm">
                    <span
                      className="grid place-items-center rounded-lg font-mono font-extrabold shrink-0"
                      style={{
                        width: 32,
                        height: 32,
                        background: 'var(--incorrect-soft)',
                        color: 'var(--incorrect)',
                      }}
                    >
                      {key.label}
                    </span>
                    <span className="font-bold tabular-nums w-16">{key.accuracy}%</span>
                    <span className="text-muted-foreground">
                      {key.attempts} presses · typed with the {key.fingerLabel}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        {tips.length > 0 && (
          <section className="mt-6">
            <h3 className="text-base mb-2">Suggestions</h3>
            <ul className="grid gap-2">
              {tips.map((tip) => (
                <li key={tip.id} className="text-sm text-muted-foreground">
                  <strong className="text-foreground">{tip.title}.</strong> {tip.body}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── course map ────────────────────────────────────────────────── */}
        <section className="mt-6">
          <h3 className="text-base mb-3">Course progress</h3>
          <ul className="grid gap-1.5">
            {levels.map((level) => (
              <li key={level.id} className="flex items-center gap-3 text-sm">
                <span className="w-40 shrink-0 truncate font-bold">
                  {level.index}. {level.title}
                </span>
                <span className="flex-1">
                  <ProgressRail value={level.pct} max={100} color={level.color} />
                </span>
                <span className="w-12 text-right tabular-nums text-muted-foreground font-bold">
                  {level.done}/{level.total}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <footer
          className="mt-6 pt-4 text-xs text-muted-foreground"
          style={{ borderTop: '2px solid var(--border)' }}
        >
          Generated from practice data stored on this device only. TypeSwift does not collect,
          transmit or store any information about your child.
        </footer>
      </div>

      {/* ── charts (screen only — they print poorly and add nothing) ───── */}
      <div className="k-no-print grid gap-4 lg:grid-cols-2">
        <SpeedChart data={speedSeries(profile)} />
        <PracticeChart data={dailyHistory(profile, range === 7 ? 7 : 30)} />
      </div>

      <p className="k-no-print text-sm text-muted-foreground">
        Looking for classroom tools instead?{' '}
        <Link href="/kids/teachers" className="font-extrabold" style={{ color: 'var(--accent)' }}>
          Open the teacher page
        </Link>
        .
      </p>
    </div>
  );
}
